import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, MapPin, Camera, Clock, ExternalLink, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (d) => (d ? new Date(d).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—');

function DistanceBadge({ m }) {
  if (m == null) return <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-xs font-medium" data-testid="review-distance"><MapPin className="w-3 h-3" /> Sin GPS en la foto</span>;
  const ok = m <= 500;
  const txt = m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid="review-distance"><MapPin className="w-3 h-3" /> {txt} del waypoint</span>;
}

export default function PhotoReviewQueue({ eventId, eventTitle, open, onOpenChange, onCountChange }) {
  const [tab, setTab] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/rally-master/events/${eventId}/review-queue`, { params: { status: tab } });
      setData(r.data);
      onCountChange?.(r.data.counts.pending);
    } catch { toast.error('No se pudo cargar la cola de revisión'); } finally { setLoading(false); }
  }, [eventId, tab, onCountChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const decide = async (photo, status) => {
    let reason = null;
    if (status === 'rejected') {
      reason = window.prompt('Motivo del rechazo (opcional):', '');
      if (reason === null) return;
    }
    setActing(photo.id);
    try {
      await axios.put(`${API}/rally-master/tracking-photos/${photo.id}/verify`, null, { params: { status, rejection_reason: reason || undefined } });
      toast.success(status === 'verified' ? `Aprobada: ${photo.waypoint?.name}` : status === 'rejected' ? `Rechazada: ${photo.waypoint?.name}` : 'Devuelta a pendientes');
      setData((d) => d && {
        ...d,
        items: d.items.filter((i) => i.id !== photo.id),
        counts: { ...d.counts, [photo.status]: Math.max(0, d.counts[photo.status] - 1), [status]: d.counts[status] + 1 },
      });
      if (photo.status === 'pending' || status === 'pending') onCountChange?.(Math.max(0, (data?.counts.pending || 0) + (status === 'pending' ? 1 : -1)));
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar la foto'); } finally { setActing(null); }
  };

  const counts = data?.counts || { pending: 0, verified: 0, rejected: 0 };
  const tabs = [['pending', 'Pendientes'], ['verified', 'Aprobadas'], ['rejected', 'Rechazadas']];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="review-queue-dialog">
        <DialogHeader className="p-5 pb-3 border-b sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5 text-violet-600" /> Cola de revisión · {eventTitle}</DialogTitle>
          <DialogDescription>Fotos de waypoints que no se validaron automáticamente por EXIF. Aprueba o rechaza en un toque.</DialogDescription>
          <div className="flex gap-2 pt-2">
            {tabs.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setTab(k)}
                className={`h-10 px-4 rounded-full text-sm font-semibold border ${tab === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}
                data-testid={`review-tab-${k}`}>
                {label} <span className={tab === k ? 'text-white/70' : 'text-gray-400'}>({counts[k]})</span>
              </button>
            ))}
          </div>
        </DialogHeader>

        <div className="p-4 space-y-3">
          {loading && <div className="py-10 text-center"><Loader2 className="w-7 h-7 animate-spin text-violet-600 mx-auto" /></div>}
          {!loading && data?.items.length === 0 && (
            <p className="py-10 text-center text-gray-500" data-testid="review-empty">
              {tab === 'pending' ? 'No hay fotos pendientes de revisión.' : 'Nada por aquí.'}
            </p>
          )}
          {!loading && data?.items.map((p) => (
            <div key={p.id} className="rounded-xl border border-gray-200 bg-white p-3 flex flex-col sm:flex-row gap-3" data-testid={`review-item-${p.id}`}>
              <a href={p.photo_url} target="_blank" rel="noreferrer" className="relative shrink-0 group">
                <img src={p.photo_url} alt="" className="w-full sm:w-44 h-44 object-cover rounded-lg border" />
                <span className="absolute bottom-1 right-1 bg-black/60 text-white rounded p-1 opacity-0 group-hover:opacity-100"><ExternalLink className="w-3.5 h-3.5" /></span>
              </a>
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-3.5 h-3.5 rounded-full border border-white shadow" style={{ backgroundColor: p.waypoint?.color || '#9ca3af' }} />
                  <p className="font-bold text-gray-900 truncate" data-testid="review-wp-name">{p.waypoint?.name || p.waypoint_name}</p>
                  {p.waypoint?.category && <span className="text-xs text-gray-500">{p.waypoint.category}{p.waypoint.points ? ` · ${p.waypoint.points} pts` : ''}</span>}
                </div>
                <p className="text-sm text-gray-800" data-testid="review-pilot">
                  {p.dorsal && <span className="inline-block bg-gray-900 text-white text-xs font-bold rounded px-1.5 mr-1.5">#{p.dorsal}</span>}
                  {p.pilot_name} <span className="text-gray-400 text-xs">{p.pilot_email}</span>
                </p>
                <div className="flex flex-wrap gap-1.5 items-center">
                  <DistanceBadge m={p.exif_distance_m} />
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Clock className="w-3 h-3" /> Foto: {fmt(p.exif_taken_at)} · Subida: {fmt(p.uploaded_at)}</span>
                </div>
                {p.validation_note && <p className="text-xs text-gray-500">{p.validation_note}</p>}
                {p.description && <p className="text-xs text-gray-700 italic">“{p.description}”</p>}
                {p.rejection_reason && <p className="text-xs text-red-700">Motivo: {p.rejection_reason}</p>}
                {p.waypoint?.latitude != null && p.exif_latitude != null && (
                  <a className="text-xs text-blue-700 underline" target="_blank" rel="noreferrer"
                    href={`https://www.google.com/maps/dir/?api=1&origin=${p.exif_latitude},${p.exif_longitude}&destination=${p.waypoint.latitude},${p.waypoint.longitude}`}>
                    Ver posición de la foto vs waypoint
                  </a>
                )}
              </div>
              <div className="flex sm:flex-col gap-2 shrink-0 sm:w-36">
                {p.status !== 'verified' && (
                  <Button type="button" disabled={acting === p.id} onClick={() => decide(p, 'verified')} className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`review-approve-${p.id}`}>
                    {acting === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-1" /> Aprobar</>}
                  </Button>
                )}
                {p.status !== 'rejected' && (
                  <Button type="button" variant="outline" disabled={acting === p.id} onClick={() => decide(p, 'rejected')} className="flex-1 h-12 border-red-300 text-red-700 hover:bg-red-50" data-testid={`review-reject-${p.id}`}>
                    <XCircle className="w-5 h-5 mr-1" /> Rechazar
                  </Button>
                )}
                {p.status !== 'pending' && (
                  <Button type="button" variant="ghost" disabled={acting === p.id} onClick={() => decide(p, 'pending')} className="h-10 text-gray-600" data-testid={`review-reopen-${p.id}`}>
                    <RotateCcw className="w-4 h-4 mr-1" /> Reabrir
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
